import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePymbleApi } from "./usePymbleApi";

const GROUPS_SEARCH_ENDPOINT = "/group/search";
const DEFAULT_GROUP_ICON =
  "https://pymblelc.blob.core.windows.net/assets/pymble-p.png";

export interface GroupDetail {
  _id: string;
  Name?: { Short?: string; Long?: string };
  Description?: { Short?: string; Long?: string };
  Icon?: string;
  CoverImage?: string | { Url?: string; [key: string]: unknown };
  // Type.Id is either a numeric Edumate type id (most groups) OR the string
  // literal "preset" for materialised preset-groups (Phase 2). Narrow uses
  // (e.g. the Group Type dropdown in Settings) must guard with typeof rather
  // than assuming number - see closes group-manager#81.
  Type?: { Id?: number | string; Label?: string };
  StartAt?: string;
  EndAt?: string | null;
  Permission?: { Policy?: string; Admin?: boolean };
  ShowMemberList?: boolean | string;
  // Flattened EFFECTIVE chat flag from the group-search projection:
  // Settings.Chat === true AND the group is chat-eligible (no student
  // audience/members). Same defensive boolean|string shape as
  // ShowMemberList. Eligibility itself is enforced server-side on the
  // settings write - never reimplement it client-side.
  Chat?: boolean | string;
  // Usually a role array (["parent","staff","student"]), but legacy/system
  // groups store a boolean. Callers must guard with Array.isArray before
  // treating it as a list (see SettingsTab).
  CanSubscribe?: string[] | boolean;
  // The single-group fetch (this hook) keeps the whole Settings subdocument
  // (only ParentGroup/StudentGroup/CurrentStudentChatMembers are excluded
  // server-side) - unlike the flattened, eligibility-gated `Chat` above, this
  // is the raw stored value. PhotoPosters absent means "editors" - see
  // pymble-apis group.helpers.photo_posters.
  Settings?: {
    PhotoPosters?: "admins" | "editors" | "members";
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface MemberRecord {
  _id: string;
  [key: string]: unknown;
}

interface NoticeRecord {
  _id: string;
  [key: string]: unknown;
}

interface EventRecord {
  _id: string;
  [key: string]: unknown;
}

/**
 * Hook to fetch a single group's details plus member/notice/event counts
 * for the group detail page.
 */
export function useGroupDetail(groupId: string) {
  const { callApi, isAuthenticated } = usePymbleApi();

  // Fetch group details
  const groupQuery = useQuery({
    queryKey: ["group-detail", groupId],
    queryFn: async () => {
      const res = await callApi(
        "GET",
        `${GROUPS_SEARCH_ENDPOINT}?group_id=${groupId}`
      );
      if (res && Array.isArray(res) && res.length > 0) {
        const group = res[0] as GroupDetail;
        group.Icon = group.Icon || DEFAULT_GROUP_ICON;
        return group;
      }
      return null;
    },
    enabled: isAuthenticated && !!groupId,
    staleTime: 1000 * 60 * 5, // 5 min
  });

  // Fetch members count
  const membersQuery = useQuery({
    queryKey: ["group-detail-members", groupId],
    queryFn: async () => {
      const res = await callApi("GET", `/group/${groupId}/members`);
      if (res && Array.isArray(res)) return res as MemberRecord[];
      return [];
    },
    enabled: isAuthenticated && !!groupId,
    staleTime: 1000 * 60 * 5, // 5 min
  });

  // Fetch notices count
  const noticesQuery = useQuery({
    queryKey: ["group-detail-notices", groupId],
    queryFn: async () => {
      const res = await callApi(
        "GET",
        `/notice/user/?group_id=${groupId}&current_year=1&show_read=1`
      );
      if (res && Array.isArray(res)) return res as NoticeRecord[];
      return [];
    },
    enabled: isAuthenticated && !!groupId,
    staleTime: 1000 * 60 * 5, // 5 min
  });

  // Fetch events count
  const eventsQuery = useQuery({
    queryKey: ["group-detail-events", groupId],
    queryFn: async () => {
      const res = await callApi(
        "GET",
        `/group/events/${groupId}?latest=true&limit=50`
      );
      if (res && Array.isArray(res)) return res as EventRecord[];
      return [];
    },
    enabled: isAuthenticated && !!groupId,
    staleTime: 1000 * 60 * 5, // 5 min
  });

  const counts = useMemo(
    () => ({
      members: membersQuery.data?.length ?? 0,
      notices: noticesQuery.data?.length ?? 0,
      events: eventsQuery.data?.length ?? 0,
    }),
    [membersQuery.data, noticesQuery.data, eventsQuery.data]
  );

  const isLoading =
    groupQuery.isLoading ||
    membersQuery.isLoading ||
    noticesQuery.isLoading ||
    eventsQuery.isLoading;

  return {
    group: groupQuery.data ?? null,
    counts,
    isLoading,
    isError: groupQuery.isError,
    members: membersQuery.data ?? [],
    notices: noticesQuery.data ?? [],
    events: eventsQuery.data ?? [],
  };
}
